import React, { useEffect, useState } from "react";

import { Link } from "react-router-dom";
import { isEmpty } from "lodash";

// import PieChart from "../../components/Charts/PieChart";
// import BarChart from "../../components/Charts/BarChart";
import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import { getCurrentDateYMD } from "../../utils/DateHelper";

import DashboardAPI from "../../API/dashboard.api";
import TableLoader from "../../components/Loaders/TableLoader";
import { isAuthorized } from "../../utils/RolesHelpers";
import { ROLES } from "../../utils/constants";
import { getRoles } from "../../utils/OktaHelpers";
import BasicBarChart from "../../components/Charts/BasicBarChart";
import InformationTooltip from "../../components/Tooltips/InformationTooltip";
import { Trans, useTranslation } from "react-i18next";
import {
    getInitialDateFilter,
    getSessionFilters,
    getMappedFilters,
} from "../../utils/FilterHelpers";
import { setObject } from "../../utils/StorageHelpers";
import { loginSubscriber } from "../../utils/Observer/ObserverService";

const Dashboard = () => {
    const [scansNumber, setScansNumber] = useState(null);
    const [activeUsersNumber, setActiveUsersNumber] = useState(null);
    const [modelsNumber, setModelsNumber] = useState(null);
    const [measurementsNumber, setMeasurementsNumber] = useState(null);
    //NEW CHARTS STATES
    const [scansPerLot, setScansPerLot] = useState(null);
    const [scansPerMaterial, setScansPerMaterial] = useState(null);

    const [isScansPerLotLoading, setIsScansPerLotLoading] = useState(false);
    const [isScansPerMaterialLoading, setIsScansPerMaterialLoading] =
        useState(false);

    const [isAdmin, setIsAdmin] = useState(null);
    const [filters, setFilters] = useState({});
    //React lazy initialization in to reduce the cost of useless calling of getSessionFilters
    const [appliedFilters, setAppliedFilters] = useState(() =>
        getSessionFilters("dashboard-filters")
    );

    const { t } = useTranslation();

    const searchFilters = [
        {
            label: t("createdAt"),
            name: "createdAt",
            type: "Date",
            monthBoundary: 3,
            max: getCurrentDateYMD(),
        },
    ];

    useEffect(() => {
        const localStorageFilters = getSessionFilters("dashboard-filters");
        //Runs once then only on loginSubscriber change
        const subscription = loginSubscriber.subscribe(() => {
            const isUserAllowed = isAuthorized(
                [
                    ROLES.superAdmin,
                    ROLES.partnerAdmin,
                    ROLES.admin,
                    ROLES.support,
                ],
                getRoles()
            );
            setIsAdmin(isUserAllowed);

            if (!isEmpty(localStorageFilters)) {
                fetchData(localStorageFilters, isUserAllowed);
                setFilters(localStorageFilters);
                setAppliedFilters(localStorageFilters);
            } else {
                const filter = getInitialDateFilter();
                setFilters(filter);
                setAppliedFilters(filter);
                fetchData(filter, isUserAllowed);
            }
        });
        //Unsubsribes when component is unmounted
        return () => {
            subscription.unsubscribe();
        };
    }, []);

    const fetchData = (filters = null, isUserAllowed = false) => {
        let newFilter = getMappedFilters(filters);

        getScansNumber(newFilter);
        getModelsNumber(newFilter);
        getScansPerLot(newFilter);
        getScansPerMaterial(newFilter);

        if (isUserAllowed) {
            getActiveUsersNumber(newFilter);
            getMeasurementsNumber(newFilter);
        }
    };

    const getScansPerLot = async (filters = null) => {
        setIsScansPerLotLoading(true);
        const _filters = getMappedFilters(filters);

        const result = await DashboardAPI.getScansPerLot(_filters);
        setScansPerLot(result || []);
        setIsScansPerLotLoading(false);
    };

    const getScansPerMaterial = async (filters = null) => {
        setIsScansPerMaterialLoading(true);
        const _filters = getMappedFilters(filters);

        const result = await DashboardAPI.getScansPerMaterial(_filters);
        setScansPerMaterial(result || []);
        setIsScansPerMaterialLoading(false);
    };

    const getScansNumber = async (filters = null) => {
        try {
            const result = await DashboardAPI.getScansNumber(filters);
            setScansNumber(result || 0);
        } catch (error) {
            console.log(error);
        }
    };

    const getActiveUsersNumber = async () => {
        try {
            const result = await DashboardAPI.getActiveUsersNumber();
            setActiveUsersNumber(result || 0);
        } catch (error) {
            console.log(error);
        }
    };

    const getModelsNumber = async (filters = null) => {
        try {
            const result = await DashboardAPI.getModelsNumber(filters);
            setModelsNumber(result || 0);
        } catch (error) {
            console.log(error);
        }
    };

    const getMeasurementsNumber = async (filters = null) => {
        try {
            const result = await DashboardAPI.getMeasurementsNumber(filters);
            setMeasurementsNumber(result || 0);
        } catch (error) {
            console.log(error);
        }
    };

    // const getModelsStatus = async (filters = null) => {
    //     try {
    //         const result = await DashboardAPI.getModelsStatus(filters);
    //         const mappedResult = result.map((record) => {
    //             return { name: record.name, y: parseInt(record.value) };
    //         });
    //         setModelsStatus(mappedResult);
    //     } catch (error) {
    //         console.log(error);
    //     }
    // };

    const applyFilters = (returnedFilters) => {
        fetchData(returnedFilters, isAdmin);
    };

    const isLoadingData = () => {
        return (
            // modelsStatus === null ||
            // (isAdmin && materialsScans === null) ||
            (isAdmin && measurementsNumber === null) ||
            modelsNumber === null ||
            (isAdmin && activeUsersNumber === null) ||
            scansNumber === null
        );
    };

    const setDashboardFilters = (entities) => {
        for (const entity of entities) {
            let filter = {};
            if (entity.isScan) {
                filter.capturedAt = appliedFilters.createdAt;
            } else {
                filter.createdAt = appliedFilters.createdAt;
            }

            setObject(entity.name, filter, true);
        }
    };

    if (isLoadingData()) return <TableLoader isLoading={true} />;
    return (
        <>
            <div id="customer-content" className="customer-content">
                <header className="main-content-header flex items-center justify-between">
                    <div className="flex justify-start">
                        <h1 className="h1 pr-1">
                            <Trans i18nKey="dashboard">Dashboard</Trans>
                        </h1>
                        <div className="self-center">
                            <InformationTooltip
                                message={t("dataTimeTooltip", {
                                    entity: t("dashboard"),
                                })}
                            />
                        </div>
                    </div>
                    <div className="action-header-wrapper flex items-center">
                        <FilterDropdown
                            filterOptions={searchFilters}
                            filters={filters}
                            setFilters={setFilters}
                            applyFilters={applyFilters}
                            component="dashboard"
                            appliedFilters={appliedFilters}
                            setAppliedFilters={setAppliedFilters}
                        />
                    </div>
                </header>
                <div className="mb-4">
                    <div className="grid-4-cols">
                        <Link to="/scans">
                            <div
                                className="card-container shadow truncate"
                                onClick={() =>
                                    setDashboardFilters([
                                        {
                                            name: "scans-standard-filters",
                                            isScan: true,
                                        },
                                        {
                                            name: "scans-lot-filters",
                                            isScan: true,
                                        },
                                    ])
                                }
                            >
                                <div className="flex justify-between">
                                    <h3 className="card-title">
                                        <Trans i18nKey="scansDone">
                                            Scans Done
                                        </Trans>
                                    </h3>
                                    <div className="icon-circle">
                                        <i className="fas fa-flask" />
                                    </div>
                                </div>
                                <div className="card-number truncate">
                                    {(+scansNumber).toLocaleString() || 0}
                                </div>
                                <span className="card-status">
                                    <Trans i18nKey="scans">Scans</Trans>
                                </span>
                            </div>
                        </Link>

                        {isAdmin && (
                            <Link to="/users">
                                <div className="card-container shadow truncate">
                                    <div className="flex justify-between">
                                        <h3 className="card-title">
                                            <Trans i18nKey="currentActiveUsers">
                                                Users Count
                                            </Trans>
                                        </h3>
                                        <div className="icon-circle">
                                            <i className="fas fa-users" />
                                        </div>
                                    </div>
                                    <div className="card-number truncate">
                                        {(+activeUsersNumber).toLocaleString() ||
                                            0}
                                    </div>
                                    <span className="card-status">
                                        <Trans i18nKey="users">Users</Trans>
                                    </span>
                                </div>
                            </Link>
                        )}

                        <Link to="/models">
                            <div
                                className="card-container shadow truncate"
                                onClick={() =>
                                    setDashboardFilters([
                                        { name: "model-table-filters" },
                                    ])
                                }
                            >
                                <div className="flex justify-between">
                                    <h3 className="card-title">
                                        <Trans i18nKey="modelsCreated">
                                            Models Created
                                        </Trans>
                                    </h3>
                                    <div className="icon-circle">
                                        <i className="far fa-chart-bar" />
                                    </div>
                                </div>
                                <div className="card-number truncate">
                                    {(+modelsNumber).toLocaleString() || 0}
                                </div>
                                <span className="card-status">
                                    <Trans i18nKey="models">Models</Trans>
                                </span>
                            </div>
                        </Link>

                        {isAdmin && (
                            <Link to="/measurements">
                                <div
                                    className="card-container shadow truncate"
                                    onClick={() =>
                                        setDashboardFilters([
                                            {
                                                name: "measurement-collect-filters",
                                            },
                                            {
                                                name: "measurement-scan-filters",
                                                isScan: true,
                                            },
                                        ])
                                    }
                                >
                                    <div className="flex justify-between">
                                        <h3 className="card-title">
                                            <Trans i18nKey="collectedSamples">
                                                Collected Samples
                                            </Trans>
                                        </h3>
                                        <div className="icon-circle">
                                            <i className="fas fa-vial" />
                                        </div>
                                    </div>
                                    <div className="card-number truncate">
                                        {(+measurementsNumber).toLocaleString() ||
                                            0}
                                    </div>
                                    <span className="card-status">
                                        <Trans i18nKey="measurements">
                                            Measurements
                                        </Trans>
                                    </span>
                                </div>
                            </Link>
                        )}
                    </div>
                </div>
                <div className="grid-2-cols chart-grid">
                    {/* {isAdmin && <BarChart data={materialsScans} />} */}

                    {/* <PieChart data={modelsStatus} /> */}
                    <BasicBarChart
                        cardTitle={t("scansBarMaterial")}
                        measurementUnit={t("scans")}
                        data={scansPerMaterial}
                        isLoading={isScansPerMaterialLoading}
                    />
                    <BasicBarChart
                        cardTitle={t("scansBarLot")}
                        measurementUnit={t("scans")}
                        data={scansPerLot}
                        isLoading={isScansPerLotLoading}
                    />
                </div>
            </div>
        </>
    );
};

export default Dashboard;
